import { inject } from "@angular/core";
import { Observable, catchError, map, of } from "rxjs";
import { ActivatedRouteSnapshot, CanActivateFn, Router, UrlTree } from "@angular/router";
import { CookieService } from "ngx-cookie-service";

import { UserService } from "../services/user.service";
import { LoggerService } from "../../shared/services/logger.service";
import { UserProfileResponse } from "../../shared/models/request/user-profile.response";
import { CONSTANTS, COOKIE, SPLUNK } from "../../modules/login/util/constants";
import { Constants } from "../../shared/constants/constants";
import { APP_CONFIG } from "../services/app-config.service";

export const autoLoginGuard: CanActivateFn = (route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> | boolean | UrlTree => {

  const userService = inject(UserService);
  const cookieService = inject(CookieService);
  const loggerService = inject(LoggerService);
  const router = inject(Router);

  // Kill switch: when enabled, /login redirects to /adlogin so the Azure SSO flow
  // happens instead of the legacy login page.
  if (route.routeConfig?.path === 'login' && APP_CONFIG.ENABLE_LOGIN_TO_ADLOGIN_REDIRECT === 'true') {
    return router.createUrlTree(['adlogin']);
  }

  const isUserNameAvailable: boolean = cookieService.check(COOKIE.DP_ADM_USER_NAME);
  const isSessionIdAvailable: boolean = cookieService.check(COOKIE.DP_ADM_SID);
  const isJwtTokenAvailable: boolean = cookieService.check(COOKIE.DP_ADM_TOKEN);
  const isUserIdAvailable: boolean = cookieService.check(COOKIE.DP_ADM_USER_ID);

  if (isUserNameAvailable && isSessionIdAvailable && isJwtTokenAvailable && isUserIdAvailable) {
    if (userService.getUserName) {
      router.navigate(['home'], { replaceUrl: true });
      return false;
    } else {
      try {
        // Restore Session ID
        const sid: string = atob(cookieService.get(COOKIE.DP_ADM_SID));
        userService.setSessionID = sid;

        // Restore JWT Token
        const token: string = atob(cookieService.get(COOKIE.DP_ADM_TOKEN));
        userService.setJwtToken = token;

        // Restore User Id
        const userId: string = atob(cookieService.get(COOKIE.DP_ADM_USER_ID));
        userService.setUserIdentifier = userId;

        // Restore User Name
        const userName: string = atob(cookieService.get(COOKIE.DP_ADM_USER_NAME));
        userService.setUserName = userName;
        const isAdLogin = localStorage.getItem('ENABLE_AZURE_LOGIN') === 'true';
        const request = { userName: userName, adlogin: isAdLogin ? 'true' : 'false' };

        // Restore AD user profile from localStorage on page refresh / new tab (Azure login only)
        if (isAdLogin) {
            const raw = localStorage.getItem('AD_USER_PROFILE');
            if (raw) {
                try {
                    const adProfile = JSON.parse(raw);
                    if (adProfile.adGroups?.length)  { userService.setAdGroups  = adProfile.adGroups;  }
                    if (adProfile.firstName)         { userService.setFirstName  = adProfile.firstName; }
                    if (adProfile.lastName)          { userService.setLastName   = adProfile.lastName;  }
                    if (adProfile.email)             { userService.setEmail      = adProfile.email;     }
                } catch (_) { /* ignore malformed value */ }
            }
        }

        // Restore User Data
        return userService.userProfileRequest(request)
          .pipe(
            map((data: UserProfileResponse) => {
              if (data?.responseHeader?.transactionNotification?.status === CONSTANTS.SUCCESS) {
                // Set user profile
                userService.setUserProfile = data.responseBody;
                // Call session timeout logic
                userService.handleSessionTimeout();
                // Set user as authenticated
                userService.setAuthenticated = true;
                userService.isAuntheticated$.next(true);

                // Splunk Update
                loggerService.logEvent(
                  SPLUNK.USER_PROFILE_SUCCESS_EVENT_CD,
                  SPLUNK.USER_PROFILE_SUCCESS_EVENT_DESC,
                  SPLUNK.USER_PROFILE_SUCCESS_ID,
                  '',
                  {
                    adminUserId: userService.getUserIdentifier,
                    adminUserName: userService.getUserName
                  },
                  CONSTANTS.SUCCESS
                );

                router.navigate(['home'], { replaceUrl: true });
                return false;
              } else {
                return navigateToLogin(userService, loggerService, userName);
              }
            }), catchError(() => {
              return of(navigateToLogin(userService, loggerService, userName));
            })
          );
      } catch (error) {
        return of(navigateToLogin(userService, loggerService, Constants.UNAUTHUSER));
      }
    }
  } else {
    userService.setAuthenticated = false;
    userService.isAuntheticated$.next(false);
    return true;
  }
};

const navigateToLogin = (
  userService: UserService,
  loggerService: LoggerService,
  userName: string
): boolean | UrlTree => {
  loggerService.logEvent(
    SPLUNK.USER_PROFILE_FAILURE_EVENT_CD,
    SPLUNK.USER_PROFILE_FAILURE_EVENT_DESC,
    SPLUNK.USER_PROFILE_FAILURE_ID,
    '',
    {
      adminUserName: userName
    },
    CONSTANTS.FAILURE
  );
  userService.isAuntheticated$.next(false);
  userService.logoutUser();
  return true;
}

